
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Grid,
  Spinner,
  Select,
} from '@chakra-ui/react'
import apiClient from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'

const translationSchema = z.object({
  language_id: z.string().min(1, 'Language is required'),
  key: z.string().min(1, 'Key is required').max(255, 'Key must be less than 255 characters'),
  value: z.string().min(1, 'Translation value is required'),
  group: z.string().optional(),
  notes: z.string().optional(),
})

type TranslationFormData = z.infer<typeof translationSchema>

interface TranslationFormProps {
  translationId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

export function TranslationForm({ translationId, onSuccess, onCancel }: TranslationFormProps) {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [languages, setLanguages] = useState<any[]>([])
  const isEditMode = !!translationId

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<TranslationFormData>({
    resolver: zodResolver(translationSchema),
    defaultValues: {
      language_id: '',
      key: '',
      value: '',
      group: '',
      notes: '',
    },
  })

  // Fetch languages on mount
  useEffect(() => {
    apiClient.get('/system-settings/language/languages')
      .then(({ data }) => {
        setLanguages(data?.data || [])
      })
      .catch((error: any) => {
        console.error('Failed to fetch languages:', error)
      })
  }, [])

  // Fetch translation data for edit mode
  useEffect(() => {
    if (isEditMode && translationId) {
      setIsLoading(true)
      apiClient
        .get(`/system-settings/language/translations/${translationId}`)
        .then(({ data }) => {
          const translationData = data?.data || data || {}

          // Reset form with all values
          reset({
            language_id: String(translationData.language_id || ''),
            key: translationData.key || '',
            value: translationData.value || '',
            group: translationData.group || '',
            notes: translationData.notes || '',
          })
        })
        .catch((error: any) => {
          showError(error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [isEditMode, translationId, reset, showError])

  const onSubmit = async (data: TranslationFormData) => {
    setIsSubmitting(true)

    try {
      const payload = {
        language_id: parseInt(data.language_id),
        key: data.key,
        value: data.value,
        group: data.group || null,
        notes: data.notes || null,
      }

      if (isEditMode) {
        await apiClient.put(`/system-settings/language/translations/${translationId}`, payload)
        showSuccess('Success', 'Translation updated successfully')
      } else {
        await apiClient.post('/system-settings/language/translations', payload)
        showSuccess('Success', 'Translation created successfully')
      }

      if (onSuccess) {
        onSuccess()
      } else {
        setTimeout(() => navigate('/system-settings/language'), 2000)
      }
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/system-settings/language')
    }
  }

  if (isLoading) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading translation...</Text>
      </VStack>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        {/* Language Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Language <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.language_id}>
            <Select
              {...register('language_id')}
              placeholder="Select a language"
            >
              {languages.map((lang) => (
                <option key={lang.id} value={lang.id}>
                  {lang.name} ({lang.code})
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.language_id?.message}</FormErrorMessage>
            <FormHelperText>Select the language for this translation.</FormHelperText>
          </FormControl>
        </Grid>

        {/* Key Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Key <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.key}>
            <Input
              {...register('key')}
              placeholder="e.g. welcome.message, error.not_found"
            />
            <FormErrorMessage>{errors.key?.message}</FormErrorMessage>
            <FormHelperText>The unique key identifier for this translation.</FormHelperText>
          </FormControl>
        </Grid>

        {/* Value Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Translation <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.value}>
            <Textarea
              {...register('value')}
              placeholder="Enter the translated text..."
              rows={4}
            />
            <FormErrorMessage>{errors.value?.message}</FormErrorMessage>
            <FormHelperText>The translated text for the key.</FormHelperText>
          </FormControl>
        </Grid>

        {/* Group Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Group</FormLabel>
          <FormControl isInvalid={!!errors.group}>
            <Input
              {...register('group')}
              placeholder="e.g. common, errors, validation"
            />
            <FormErrorMessage>{errors.group?.message}</FormErrorMessage>
            <FormHelperText>Optional group name to organize translations.</FormHelperText>
          </FormControl>
        </Grid>

        {/* Notes Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Notes</FormLabel>
          <FormControl isInvalid={!!errors.notes}>
            <Textarea
              {...register('notes')}
              placeholder="Optional notes about this translation..."
              rows={3}
            />
            <FormErrorMessage>{errors.notes?.message}</FormErrorMessage>
            <FormHelperText>Optional notes or context for this translation.</FormHelperText>
          </FormControl>
        </Grid>

        {/* Action Buttons */}
        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button
              variant="outline"
              onClick={handleCancel}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create Translation'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}

